import 'payments/payments-dialog/styles.css'
import * as React from 'react'
import { tw } from 'utils/classnames'
import { localStore } from 'utils/storage'
import { useIsLoggedInQuery } from 'auth/hooks'
import { UsersOnline } from '@repo/studio-components/users-online-indicator'
import { CoinBalance } from '@repo/studio-components/coin-balance'
import { ComponentBoundary } from '@repo/studio-components-boundary'
import { GoProForFreeButton } from '@repo/studio-components/top-app-bar/components/go-pro-for-free'
import { EarnButton, EarnIconButton } from '@repo/studio-components/earn-button'
import {
  EarnMoreCoinsButton,
  EarnMoreCoinsIconButton,
} from '@repo/studio-components/earn-more-coins-button'
import { useUserSubscriptionVariantQuery } from '@repo/cf-user/hooks/use-user-data'
import { TITLEBAR_ELEMENT_ID } from '@repo/studio-desktop-bridge/window-chrome'
import { isStudioDesktop } from '@repo/studio-desktop-bridge/runtime'
import { AuthButton, AuthTrigger } from '@repo/studio-components/auth/auth-button'
import type { ButtonVariant } from '@repo/studio-design-system-ui-button'
import { Auth } from '@repo/studio-components/top-app-bar/components/auth'
import { useUsersOnlineCount } from '@repo/studio-online-presence/hooks/use-users-online-count'
import { useIsCompactViewport } from '@repo/studio-hooks-dom/use-breakpoint'
import { NavigationSideBar, NavigationSideSheet } from './components/navigation'
import { StudioLayoutShell } from './layout-shell'
import { StudioLayoutScrollProvider } from './hooks/use-studio-layout-reset-view'
import { StudioBanners } from '@repo/studio-components-top-banner'
import { TrialActivationConfirmDialogHost } from '@repo/studio-components-trial-activation-confirm-dialog'
import { ToggleSidebarButton } from './components/toggle-sidebar-button'
import {
  StudioConfigProvider,
  StudioLayoutContext,
  useStudioConfig,
  useStudioLayoutLogo,
  useStudioLayoutLogoSlot,
} from '@repo/studio-config/studio-config-context'
import {
  StudioLayoutLink,
  useStudioMatchPath,
  useStudioPathname,
} from '@repo/studio-config/studio-config-router'
import { useActiveContextualNavigation } from './components/navigation/contextual-navigation'
import type {
  CompactNavigationMenuConfig,
  ContextualNavigationConfig,
  StudioLayoutLinkComponent,
  NavigationConfig,
  StudioNavigationMode,
  StudioTranslateFn,
} from '@repo/studio-config/studio-config-context'

const SIDEBAR_OPEN_STATE = 'studio_home_sidebar_open_state'

function getSidebarState() {
  const stored = localStore.getString(SIDEBAR_OPEN_STATE)
  // oxlint-disable-next-line no-negated-condition
  return stored !== null ? stored === 'true' : null
}

function setSidebarState(value: boolean) {
  localStore.setString(SIDEBAR_OPEN_STATE, String(value))
}

function subscribeSidebarState(callback: () => void) {
  window.addEventListener('storage', callback)
  return () => window.removeEventListener('storage', callback)
}

// Expanded by default on desktop, contracted on tablet - superseded by the local storage state
function useSidebarState() {
  const isCompactViewport = useIsCompactViewport()
  const storedState = React.useSyncExternalStore(subscribeSidebarState, getSidebarState, () => null)

  // oxlint-disable-next-line no-negated-condition
  const open = storedState !== null ? storedState : !isCompactViewport

  const setOpen = React.useCallback((value: boolean) => {
    setSidebarState(value)
    window.dispatchEvent(new Event('storage'))
  }, [])

  return [open, setOpen] as const
}

const fallback = (
  <div className="rounded-2 h-6 w-16 animate-pulse bg-gray-800 md:w-56" key="fallback" />
)

const defaultComponents = {
  StudioBanners,
  ToggleSidebarButton,
  NavigationSideBar,
  NavigationSideSheet,
  TopBarRightItems,
}

interface StudioLayoutProps {
  className?: string
  children: React.ReactNode
  LinkComponent?: StudioLayoutLinkComponent
  accountSettingsDialog?: React.ReactNode
  navigationConfig?: NavigationConfig
  compactNavigationMenus?: CompactNavigationMenuConfig[]
  /** `'none'` for a route that renders its own navigation. Defaults to `'sidebar'`. */
  navigation?: StudioNavigationMode
  topBarActions?: React.ReactNode
  contextualNavigation?: ContextualNavigationConfig[]
  logo?: React.ReactNode
  /**
   * Translate function for the chrome's strings. The package is i18n-library
   * agnostic, so this must be provided here or via `StudioConfigProvider`.
   */
  t?: StudioTranslateFn
  /** Only used by tests for dependency injection of layout chrome. */
  _components?: Partial<typeof defaultComponents>
}
type StudioLayoutProviderProps = Omit<
  StudioLayoutProps,
  'className' | 'children' | '_components'
> & {
  children: React.ReactNode
}

function StudioLayoutProvider({
  compactNavigationMenus,
  contextualNavigation,
  LinkComponent,
  accountSettingsDialog,
  navigationConfig,
  navigation,
  topBarActions,
  logo,
  t,
  children,
}: StudioLayoutProviderProps) {
  const parentContext = React.useContext(StudioLayoutContext)
  const studioConfig = useStudioConfig()
  const contextValue = React.useMemo(() => {
    return {
      // Everything the host configured passes through; only the fields the layout
      // itself can override are named below. Listing the passthrough fields one by
      // one instead meant each new config field silently stopped reaching the
      // components under the layout — routing adapters and `setUrlLocaleSegment`
      // were both lost that way.
      ...studioConfig,
      LinkComponent: LinkComponent ?? parentContext.LinkComponent ?? studioConfig?.LinkComponent,
      accountSettingsDialog:
        accountSettingsDialog ??
        parentContext.accountSettingsDialog ??
        studioConfig?.accountSettingsDialog,
      navigationConfig:
        navigationConfig ?? parentContext.navigationConfig ?? studioConfig?.navigationConfig,
      compactNavigationMenus:
        compactNavigationMenus ??
        parentContext.compactNavigationMenus ??
        studioConfig?.compactNavigationMenus,
      navigation: navigation ?? parentContext.navigation ?? studioConfig?.navigation,
      topBarActions: topBarActions ?? parentContext.topBarActions ?? studioConfig?.topBarActions,
      logo: logo ?? parentContext.logo ?? studioConfig?.logo,
      logoSlot: parentContext.logoSlot ?? studioConfig?.logoSlot,
      t: t ?? studioConfig?.t,
      contextualNavigation:
        contextualNavigation ??
        parentContext.contextualNavigation ??
        studioConfig?.contextualNavigation,
    }
  }, [
    LinkComponent,
    accountSettingsDialog,
    compactNavigationMenus,
    contextualNavigation,
    navigationConfig,
    navigation,
    topBarActions,
    logo,
    t,
    parentContext,
    studioConfig,
  ])

  return (
    <StudioLayoutContext.Provider value={contextValue}>
      <StudioConfigProvider config={contextValue}>
        {children}
        <React.Suspense fallback={null}>{contextValue.accountSettingsDialog}</React.Suspense>
      </StudioConfigProvider>
    </StudioLayoutContext.Provider>
  )
}

function StudioLayoutRoot({
  className,
  children,
  _components,
  ...providerProps
}: StudioLayoutProps) {
  return (
    <StudioLayoutProvider {...providerProps}>
      <StudioLayoutInner className={className} _components={_components}>
        {children}
      </StudioLayoutInner>
    </StudioLayoutProvider>
  )
}

function StudioLayoutInner(
  props: Omit<
    StudioLayoutProps,
    | 'LinkComponent'
    | 'accountSettingsDialog'
    | 'compactNavigationMenus'
    | 'contextualNavigation'
    | 'navigationConfig'
    | 'navigation'
    | 'logo'
  >,
) {
  const components = { ...defaultComponents, ...props._components }
  const Banners = components.StudioBanners
  const Sidebar = components.NavigationSideBar
  const SideSheet = components.NavigationSideSheet
  const SidebarToggle = components.ToggleSidebarButton
  const TopBarItems = components.TopBarRightItems
  const studioConfig = useStudioConfig()
  const compactNavigationMenus = studioConfig?.compactNavigationMenus ?? []
  const pathname = useStudioPathname()
  const matchPath = useStudioMatchPath()
  const contextualNavigation = useActiveContextualNavigation()
  const defaultCompactNavigationMenuId = compactNavigationMenus.find(menu =>
    menu.defaultPathPattern ? matchPath(menu.defaultPathPattern, pathname) : false,
  )?.id
  const defaultActiveMenuId = contextualNavigation?.id ?? defaultCompactNavigationMenuId ?? null
  const showsNavigation = studioConfig?.navigation !== 'none'
  const isCompactViewport = useIsCompactViewport()
  const [sidebarOpen, setSidebarOpen] = useSidebarState()
  const [sheetOpen, setSheetOpen] = React.useState(false)
  const [activeMenuId, setActiveMenuId] = React.useState<string | null>(null)
  const headerRef = React.useRef<HTMLElement | null>(null)
  const contentRef = React.useRef<HTMLDivElement | null>(null)

  const handleSheetOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setActiveMenuId(nextOpen ? defaultActiveMenuId : null)
      setSheetOpen(nextOpen)
    },
    [defaultActiveMenuId],
  )

  // Hamburger chrome is CSS-hidden at lg, but the sheet is not. Closing it
  // here is what lets the desktop (horizontal) language switcher take over.
  React.useLayoutEffect(() => {
    if (!isCompactViewport) {
      handleSheetOpenChange(false)
    }
  }, [handleSheetOpenChange, isCompactViewport])

  const toggleSidebar = React.useCallback(() => {
    setSidebarOpen(!sidebarOpen)
  }, [sidebarOpen, setSidebarOpen])

  const toggleSheet = React.useCallback(() => {
    handleSheetOpenChange(!sheetOpen)
  }, [handleSheetOpenChange, sheetOpen])

  return (
    <StudioLayoutShell className={props.className}>
      <Banners />
      <StudioLayoutTopBar
        headerRef={headerRef}
        leading={
          showsNavigation ? (
            <>
              <div className="hidden lg:contents">
                <SidebarToggle onClick={toggleSidebar} openOrExpanded={sidebarOpen} />
              </div>
              <div className="lg:hidden">
                <SidebarToggle onClick={toggleSheet} openOrExpanded={sheetOpen} />
              </div>
            </>
          ) : null
        }
        TopBarItems={TopBarItems}
      />
      <div className="flex min-h-0 shrink grow items-stretch">
        {showsNavigation ? (
          <>
            <div className="hidden lg:flex">
              <Sidebar open={sidebarOpen} />
            </div>
            <SideSheet
              open={sheetOpen && isCompactViewport}
              onOpenChange={handleSheetOpenChange}
              activeMenuId={activeMenuId}
              onActiveMenuIdChange={setActiveMenuId}
              headerRef={headerRef}
            />
          </>
        ) : null}
        <StudioLayoutScrollProvider contentRef={contentRef}>
          <div
            ref={contentRef}
            className="scroller-xs shrink grow"
            data-testid="studio-layout-content"
          >
            {props.children}
          </div>
        </StudioLayoutScrollProvider>
      </div>
      <TrialActivationConfirmDialogHost />
    </StudioLayoutShell>
  )
}

function StudioLayoutTopBar({
  children,
  leading,
  headerRef,
  TopBarItems = TopBarRightItems,
}: {
  children?: React.ReactNode
  leading?: React.ReactNode
  headerRef?: React.Ref<HTMLElement>
  TopBarItems?: typeof TopBarRightItems
}) {
  const studioConfig = useStudioConfig()
  const topBarActions = studioConfig?.topBarActions

  return (
    <header
      ref={headerRef}
      // Stable hook for the Electron desktop shell (macOS), which lives
      // outside React and so can't reach `headerRef`: the preload samples this
      // element's computed theme colors and injects CSS keyed on this id that
      // turns the header into the draggable titlebar and keeps it ordered
      // above banners at the window top. Renaming or removing the id breaks
      // the desktop app's window chrome.
      id={TITLEBAR_ELEMENT_ID}
      className="flex max-h-[--top-app-bar-height] min-h-[--top-app-bar-height] items-center justify-between border-b border-b-gray-800 bg-gray-900 py-2 pl-2 pr-[12px] sm:pr-[20px] z-[1]"
    >
      <div className="flex items-center">
        {leading}
        <LogoSlot />
      </div>
      <div className="ml-2 flex items-center gap-[10px] sm:ml-3">
        {children}
        <ComponentBoundary fallbackLoading={fallback} fallbackError={null}>
          <TopBarItems />
        </ComponentBoundary>
        <React.Suspense fallback={null}>{topBarActions}</React.Suspense>
        <TopBarAuth />
      </div>
    </header>
  )
}

function LogoSlot() {
  const logoSlot = useStudioLayoutLogoSlot()
  const logo = useStudioLayoutLogo()

  if (logoSlot) {
    return <div className="ml-1 md:ml-2 flex items-center">{logoSlot}</div>
  }

  return (
    <StudioLayoutLink aria-label="Go to home" className="ml-1 md:ml-2 flex items-center" to="/">
      {logo}
    </StudioLayoutLink>
  )
}

function TopBarRightItems() {
  const isLoggedInQuery = useIsLoggedInQuery()
  const isLoggedIn = isLoggedInQuery.data === true
  const isLoggedInLoading = isLoggedInQuery.isPending
  const isTopBarReady = useTopBarSuspenseQuery()

  if (!isTopBarReady || isLoggedInLoading) {
    return fallback
  }

  return (
    <>
      {/* Below md, the online count and earn actions live in the navigation side sheet. */}
      <div className="hidden md:contents">
        <UsersOnline />
      </div>
      {isLoggedIn ? (
        <ComponentBoundary fallbackLoading={null} fallbackError={null}>
          <CoinBalance className="flex" />
        </ComponentBoundary>
      ) : null}
      <div className="hidden md:contents lg:hidden">
        <TopBarIconActions earnVariant={isLoggedIn ? 'promotional' : 'tertiary'} />
      </div>
      <div className="hidden lg:contents">
        <EarnButton variant={isLoggedIn ? 'promotional' : 'tertiary'} />
        <EarnMoreCoinsButton variant={isLoggedIn ? 'secondary' : 'tertiary'} />
      </div>
    </>
  )
}

function TopBarIconActions({ earnVariant }: { earnVariant: ButtonVariant }) {
  const { data: subscriptionType } = useUserSubscriptionVariantQuery('studioAi')

  return (
    <>
      <EarnIconButton variant={earnVariant} />
      {subscriptionType === 'subscribed' ? <EarnMoreCoinsIconButton /> : null}
    </>
  )
}

function TopBarAuth() {
  const isLoggedInQuery = useIsLoggedInQuery()
  const isLoggedIn = isLoggedInQuery.data === true
  const isLoggedInLoading = isLoggedInQuery.isPending

  if (isLoggedInLoading) {
    return null
  }

  if (isLoggedIn) {
    return (
      <>
        {isStudioDesktop() ? null : (
          <div className="hidden md:contents">
            <GoProForFreeButton variant="promotional" />
          </div>
        )}
        <Auth />
      </>
    )
  }

  return (
    <>
      <AuthButton>
        <AuthTrigger variant="secondary" />
      </AuthButton>
      {isStudioDesktop() ? null : (
        <div className="hidden md:contents">
          <GoProForFreeButton variant="promotional" />
        </div>
      )}
    </>
  )
}

// TODO-REACT-19: use suspense abort value instead - problem with online count is that it's using a subscription 👻
export function useTopBarSuspenseQuery(): boolean {
  const usersOnlineCount = useUsersOnlineCount()
  const isUsersOnlineReady = usersOnlineCount !== undefined
  return isUsersOnlineReady
}

export const StudioLayout = Object.assign(StudioLayoutRoot, {
  Provider: StudioLayoutProvider,
  TopBar: StudioLayoutTopBar,
})
